import React, { useMemo } from 'react'
import { ThreadMessageCard, ThreadMessageData } from 'components/Email'
import { EmailMessage } from 'models/emailIntegration'
import {
  buildActionContext,
  useEmailThreadActions,
  EmailDealActionCallbacks,
} from './emailActions'
import { ComposeMode } from './ComposePanel'

interface ThreadMessageProps extends EmailDealActionCallbacks {
  message: EmailMessage
  isLast: boolean
  onCompose?: (mode: ComposeMode, message: EmailMessage) => void
  assignDealSubContent?: React.ReactNode
  isHidden?: boolean
  isOwnThread?: boolean
}

// Detects an HTML body without a Content-Type hint. Nylas returns the
// raw `text/html` part when available and falls back to the `text/plain`
// part when the sender only supplied that, but the API surface flattens
// both into a single `body` field. Routing plain text into `htmlBody`
// makes `\r\n` and `>` quoting render as a wall of unbroken text.
// The regex matches an opening HTML tag: `<` followed by a letter and
// then whitespace, `>`, or `/`. Avoids false positives on bare email
// addresses like `<user@example.com>` (the `@` isn't in the trailing
// character class) and entity-encoded `&lt;` (no real `<`).
const HTML_TAG_RE = /<[a-zA-Z]+(\s|>|\/)/

/** Adapter: map an `EmailMessage` to the shared `ThreadMessageData` shape. */
function toThreadMessageData(message: EmailMessage): ThreadMessageData {
  const senderInfo = message.sender?.[0]
  const body = message.body || ''
  const looksLikeHtml = HTML_TAG_RE.test(body)
  return {
    id: message.id,
    sender: {
      name: senderInfo?.name || 'Unknown',
      email: senderInfo?.email || '',
    },
    toRecipients: (message.to || []).map((c) => ({
      name: c.name || '',
      email: c.email,
    })),
    ccRecipients: (message.cc || []).map((c) => ({
      name: c.name || '',
      email: c.email,
    })),
    date: message.date,
    htmlBody: looksLikeHtml ? body : null,
    textBody: looksLikeHtml ? null : body,
    attachments: (message.attachments || []).map((a) => ({
      key: a.attachment_id,
      filename: a.filename,
      size: a.size,
      contentType: a.content_type,
      isInline: a.is_inline,
    })),
  }
}

/**
 * Thin adapter around the shared `ThreadMessageCard` that keeps the inbox's
 * deal-aware kebab menu + quick-reply dropdown, while rendering the message
 * chrome via the shared component.
 */
export function ThreadMessage({
  message,
  isLast,
  onCompose,
  assignDealSubContent,
  isHidden,
  isOwnThread,
  ...dealCallbacks
}: ThreadMessageProps) {
  const menuItems = useEmailThreadActions(
    buildActionContext(
      dealCallbacks,
      onCompose ? (mode) => onCompose(mode, message) : undefined,
      assignDealSubContent,
      isHidden,
      isOwnThread
    )
  )

  const data = useMemo(() => toThreadMessageData(message), [message])

  return (
    <ThreadMessageCard
      message={data}
      isLast={isLast}
      menuItems={menuItems}
      onQuickCompose={
        isOwnThread === true && onCompose
          ? (mode) => onCompose(mode, message)
          : undefined
      }
    />
  )
}
